import React, { createContext, useContext, ReactNode } from 'react';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from '../../context';
import { TabItem, TabsProps } from '_lex/_atoms/Tabs';
import { useSearchParams } from 'react-router-dom';

interface DashboardContextType {
  tabs: TabsProps | undefined;
  availableTabValues: string[];
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

interface DashboardProviderProps {
  children: ReactNode;
}

export function DashboardProvider({ children }: DashboardProviderProps) {
  const { hasPermission, isFeatureEnabled, currentOrganizationId } = useContext(AppContext);
  const [searchParams, setSearchParams] = useSearchParams();

  // Check if user has policy access via org permission
  const hasPolicyAccess = hasPermission('accessPolicyActivityCenter', currentOrganizationId || '');

  // Check if user has training access via org permission
  const hasTrainingAccess = hasPermission('accessTrainingActivityCenter', currentOrganizationId || '');

  // Check if user has Lexipol Reports access via feature flag AND org permission
  const hasLexipolReportsAccess =
    isFeatureEnabled('performanceReportingManager') && hasPermission('viewFieldBank', currentOrganizationId || '');

  const allTabItems: Array<{ tab: TabItem; visible: boolean }> = [
    {
      tab: {
        label: t('dashboard.tabs.lexipolReports'),
        value: 'lexipol-reports',
      },
      visible: hasLexipolReportsAccess,
    },
    {
      tab: {
        label: t('dashboard.tabs.policy'),
        value: 'policy',
      },
      visible: hasPolicyAccess,
    },
    {
      tab: {
        label: t('dashboard.tabs.training'),
        value: 'training',
      },
      visible: hasTrainingAccess,
    },
  ];

  const tabItems: TabItem[] = allTabItems.filter((item) => item.visible).map((item) => item.tab);
  const availableTabValues = tabItems.map((item) => String(item.value));

  // Determine current tab - use first available tab if current tab is not available
  const currentTabParam = searchParams.get('tab');
  const currentTab =
    currentTabParam && availableTabValues.includes(currentTabParam)
      ? currentTabParam
      : availableTabValues[0] || 'policy';

  const tabs: TabsProps | undefined =
    tabItems.length <= 1
      ? undefined
      : {
          items: tabItems,
          value: currentTab,
          onChange: (event, newValue) => {
            setSearchParams({ tab: newValue as string });
          },
        };

  const value = {
    tabs,
    availableTabValues,
  };

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
}

export function useDashboard() {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
}
